import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FilterDropdown from '../common/FilterDropdown';
import ActionButton from '../common/ActionButton';

interface Draft {
  draft_id: string;
  draft_number: string;
  status: string;
  payment_status?: string;
  subtotal?: number;
  shipping_cost?: number;
  tax_amount?: number;
  total_amount: number;
  notes?: string;
  created_at: string;
  expires_at?: string;
  converted_to_order_id?: string;
}

interface DraftsTableProps {
  drafts: Draft[];
  onShare?: (draft: Draft) => void;
  onCheckout?: (draftId: string) => void;
  onDelete?: (draft: Draft) => void;
  showAllLink?: boolean;
  compact?: boolean;
  showHeader?: boolean;
  // Inline filters
  statusFilter?: string[];
  paymentStatusFilter?: string[];
  onStatusFilterChange?: (values: string[]) => void;
  onPaymentStatusFilterChange?: (values: string[]) => void;
  availableStatuses?: string[];
  availablePaymentStatuses?: string[];
  showFilters?: boolean;
  // Sorting
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

const DraftsTable: React.FC<DraftsTableProps> = ({
  drafts,
  onShare,
  onCheckout,
  onDelete,
  showAllLink = false,
  compact = false,
  showHeader = true,
  statusFilter = [],
  paymentStatusFilter = [],
  onStatusFilterChange,
  onPaymentStatusFilterChange,
  availableStatuses = [],
  availablePaymentStatuses = [],
  showFilters = false,
  sortBy = 'created_at',
  sortOrder = 'desc',
  onSort
}) => {
  const navigate = useNavigate();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const getDaysUntilExpiry = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const today = new Date();
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDraftNumber = (draftNumber: string) => {
    const parts = draftNumber.split('-');
    if (parts.length >= 4) {
      return `DFT-${parts[2]}-${parts[3]}`;
    }
    return `DFT-${draftNumber.slice(-8)}`;
  };

  // Check if all drafts have the same status (hide filter if only one unique value)
  const hasMultipleStatuses = () => {
    const uniqueStatuses = new Set(drafts.map(draft => draft.status));
    return uniqueStatuses.size > 1;
  };

  // Check if all drafts have the same payment status (hide filter if only one unique value)
  const hasMultiplePaymentStatuses = () => {
    const uniquePaymentStatuses = new Set(drafts.map(draft => draft.payment_status || 'NOT_REQUIRED'));
    return uniquePaymentStatuses.size > 1;
  };

  return (
    <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '8px', overflow: 'hidden' }}>
      {showHeader && (
        <div className="card-header text-white" style={{ background: '#4D64C1', padding: '15px 20px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ fontSize: '1.3rem', fontWeight: '500' }}>
              <i className="fas fa-file-alt me-2"></i>Recent Drafts
            </div>
            {showAllLink && (
              <Link to="/drafts" className="btn btn-sm btn-outline-light" style={{ borderRadius: '4px' }}>
                View All <i className="fas fa-arrow-right ms-1"></i>
              </Link>
            )}
          </div>
        </div>
      )}
      <div className="card-body p-0">
        <div style={{ overflowX: 'auto', width: '100%', overflowY: 'hidden' }}>
          <table style={{
            borderCollapse: 'separate',
            borderSpacing: '0',
            minWidth: '1202px',
            tableLayout: 'fixed',
            width: '100%',
            marginBottom: '0',
            backgroundColor: 'transparent',
            border: 'none'
          }}>
            <thead style={{
              position: 'sticky',
              top: 0,
              background: '#4D64C1',
              zIndex: 10
            }}>
              <tr>
                <th
                  style={{
                    width: '110px',
                    fontSize: '1.05rem',
                    padding: '12px 8px',
                    fontWeight: '500',
                    color: 'white',
                    borderBottom: 'none',
                    cursor: onSort ? 'pointer' : 'default',
                    userSelect: 'none'
                  }}
                  onClick={() => onSort?.('created_at')}
                >
                  Date
                  {onSort && (
                    <i
                      className={`fas fa-sort-${sortBy === 'created_at' ? (sortOrder === 'asc' ? 'up' : 'down') : 'down'} ms-1`}
                      style={{ fontSize: '0.85rem', opacity: sortBy === 'created_at' ? 1 : 0.5 }}
                    ></i>
                  )}
                </th>
                <th style={{ width: '180px', fontSize: '1.05rem', padding: '12px 8px', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Draft Number
                </th>
                <th style={{ width: '160px', fontSize: '1.05rem', padding: '12px 8px', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Status
                  {showFilters && onStatusFilterChange && hasMultipleStatuses() && (
                    <FilterDropdown
                      options={availableStatuses}
                      selectedValues={statusFilter}
                      onChange={onStatusFilterChange}
                      label="Status"
                    />
                  )}
                </th>
                <th style={{ width: '160px', fontSize: '1.05rem', padding: '12px 8px 12px 16px', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Payment
                  {showFilters && onPaymentStatusFilterChange && hasMultiplePaymentStatuses() && (
                    <FilterDropdown
                      options={availablePaymentStatuses}
                      selectedValues={paymentStatusFilter}
                      onChange={onPaymentStatusFilterChange}
                      label="Payment"
                    />
                  )}
                </th>
                <th style={{ width: '200px', fontSize: '1.05rem', padding: '12px 70px 12px 8px', textAlign: 'right', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Total
                </th>
                <th style={{ width: '452px', fontSize: '1.05rem', padding: '12px 8px', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody style={{ fontSize: '1.05rem' }}>
              {drafts.map((draft) => {
                const draftDate = formatDate(draft.created_at);
                const daysUntilExpiry = draft.expires_at ? getDaysUntilExpiry(draft.expires_at) : null;

                // Derive status label similar to Orders
                let statusLabel: string = '';
                let statusColor: string = '#000000'; // Default to black
                if (draft.status === 'CONVERTED') {
                  statusLabel = 'Converted';
                  statusColor = '#000000'; // Black
                } else if (draft.expires_at) {
                  const now = new Date();
                  const exp = new Date(draft.expires_at);
                  if (exp < now) {
                    statusLabel = 'Expired';
                    statusColor = '#dc3545'; // Red for expired
                  } else {
                    statusLabel = `Expires ${formatDate(draft.expires_at)}`;
                    statusColor = '#000000'; // Black for active/valid drafts
                  }
                } else {
                  statusLabel = (draft.status || 'Draft').toString().replace(/_/g, ' ').toUpperCase();
                  // Check if it's a problem status
                  const problemStatuses = ['cancelled', 'rejected', 'expired', 'failed'];
                  const statusLower = draft.status?.toLowerCase() || '';
                  statusColor = problemStatuses.includes(statusLower) ? '#dc3545' : '#000000';
                }

                // Payment column mapping
                const paymentLabel: string = draft.status === 'CONVERTED'
                  ? (draft.payment_status && draft.payment_status.toUpperCase() === 'PAID' ? 'Paid' : 'Paid')
                  : 'Not Converted';
                // Red for problem payment statuses, black for everything else
                const problemPaymentStatuses = ['failed', 'cancelled'];
                const paymentStatusLower = draft.payment_status?.toLowerCase() || '';
                const paymentColor: string = problemPaymentStatuses.includes(paymentStatusLower) ? '#dc3545' : '#000000';

                return (
                  <tr key={draft.draft_id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '1rem', fontWeight: 'bold' }}>
                      {draftDate}
                    </td>
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '1rem' }}>
                      <Link
                        to={`/drafts/${draft.draft_id}`}
                        className="text-decoration-none fw-bold"
                        style={{
                          color: '#212529'
                        }}
                      >
                        {formatDraftNumber(draft.draft_number)}
                      </Link>
                    </td>
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 600, textTransform: 'uppercase', color: statusColor }}>
                        {statusLabel}
                      </span>
                      {statusLabel.startsWith('Expires') && daysUntilExpiry && daysUntilExpiry <= 7 && (
                        <small className="ms-2" style={{ color: '#dc3545', fontWeight: 500 }}>({daysUntilExpiry} days)</small>
                      )}
                    </td>
                    <td style={{ padding: '10px 8px 10px 16px', verticalAlign: 'middle', fontSize: '1rem' }}>
                      <span style={{ fontWeight: 600, textTransform: 'uppercase', color: paymentColor }}>
                        {paymentLabel}
                      </span>
                    </td>
                    <td style={{
                      padding: '10px 70px 10px 8px',
                      verticalAlign: 'middle',
                      fontSize: '1rem',
                      textAlign: 'right',
                      fontWeight: 'bold'
                    }}>
                      {formatCurrency(draft.total_amount)}
                    </td>
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '1rem', minWidth: '452px' }}>
                      <div className="d-flex gap-1" style={{ justifyContent: 'flex-start' }}>
                        <ActionButton
                          variant="primary"
                          onClick={() => navigate(`/drafts/${draft.draft_id}`)}
                          title="View Draft"
                        >
                          View
                        </ActionButton>
                        {draft.status === 'CONVERTED' && draft.converted_to_order_id ? (
                          <ActionButton
                            variant="success"
                            onClick={() => navigate(`/orders/${draft.converted_to_order_id}`)}
                            title="Reorder"
                          >
                            Reorder
                          </ActionButton>
                        ) : (
                          onShare && (
                            <ActionButton
                              variant="secondary"
                              onClick={() => onShare(draft)}
                              title="Share Draft"
                            >
                              Share
                            </ActionButton>
                          )
                        )}
                        {onCheckout && draft.status !== 'CONVERTED' && (
                          <ActionButton
                            variant="success"
                            onClick={() => onCheckout(draft.draft_id)}
                            title="Checkout"
                          >
                            Checkout
                          </ActionButton>
                        )}
                        {draft.status === 'CONVERTED' && draft.converted_to_order_id && (
                          <ActionButton
                            variant="info"
                            onClick={() => navigate(`/orders/${draft.converted_to_order_id}`)}
                            title="View Order"
                          >
                            View Order
                          </ActionButton>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(draft)}
                            title="Delete Draft"
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc3545',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              fontSize: '18px'
                            }}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {drafts.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center" style={{ padding: '20px', color: '#6c757d' }}>
                    <i className="fas fa-info-circle me-2"></i> No drafts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DraftsTable;